import React, { CSSProperties, ReactElement, ReactNode, RefObject, useEffect } from 'react'
import { Flex, ThemeUIStyleObject } from 'theme-ui'
import { AutoSizer } from 'react-virtualized/dist/es/AutoSizer'
import { Grid } from 'react-virtualized/dist/es/Grid'
import { WindowScroller } from 'react-virtualized/dist/es/WindowScroller'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'

const defaultItemHeight = 200

export type SizePerBreakpoint = [number, number, number]
export type GapPerBreakpoint = [string, string, string] | 0

interface CellRendererProps {
  key: string
  columnIndex: number
  isScrolling: boolean
  isVisible: boolean
  rowIndex: number
  style: CSSProperties
}

interface VirtualizedGridProps {
  dataTestId?: string
  itemCount: number
  itemHeightsPx?: SizePerBreakpoint
  itemRenderer: (index: number) => ReactElement | null
  itemsPerRow?: SizePerBreakpoint
  gap?: GapPerBreakpoint
  overscanRowCount?: number
  sx?: ThemeUIStyleObject
  scrollingContainerRef: RefObject<HTMLDivElement>
  tabIndex?: number
  ariaLabel: string
  placeholder?: (index?: number) => ReactNode
}

export default function VirtualizedGrid({
  dataTestId,
  itemCount = 0,
  itemHeightsPx = [defaultItemHeight, defaultItemHeight, defaultItemHeight],
  itemRenderer,
  itemsPerRow = [1, 1, 2],
  gap = ['1.6rem', '1.6rem', '2.4rem'],
  sx,
  scrollingContainerRef,
  tabIndex,
  ariaLabel,
  placeholder,
}: VirtualizedGridProps): ReactElement | null {
  const breakpoint = useBreakpointIndex()
  const isMobile = breakpoint === 0

  useEffect(() => {
    scrollingContainerRef.current?.scrollTo(0, 0)
  }, [scrollingContainerRef])

  // Make sure everything's ready for proper size calc
  if (breakpoint < 0 || !itemCount || !scrollingContainerRef.current) {
    return null
  }

  const columnCount = itemsPerRow[breakpoint]
  const rowHeight = itemHeightsPx[breakpoint]
  const columnGap = gap && gap[breakpoint]

  return (
    <Flex
      data-testid={dataTestId}
      sx={{
        flexDirection: 'column',
        flex: '1 1 auto',
        ...sx,
      }}
    >
      <WindowScroller scrollElement={scrollingContainerRef.current}>
        {({ height = 0, isScrolling, scrollTop, onChildScroll }) => (
          <AutoSizer disableHeight>
            {({ width }) => {
              return (
                <Grid
                  autoHeight
                  aria-label={ariaLabel}
                  isScrolling={isScrolling}
                  onScroll={onChildScroll}
                  scrollTop={scrollTop}
                  width={width}
                  height={height || 0}
                  overscanRowCount={isMobile ? 8 : 14}
                  cellRenderer={({
                    columnIndex,
                    key,
                    isVisible,
                    rowIndex,
                    style,
                  }: CellRendererProps) => {
                    const item = itemRenderer(rowIndex * columnCount + columnIndex)

                    if (!item) {
                      return
                    }

                    const padding = `0 ${
                      columnIndex === columnCount - 1 ? 0 : `calc(${columnGap} / 2)`
                    } ${columnGap} ${columnIndex === 0 ? 0 : `calc(${columnGap} / 2)`}`

                    return (
                      <Flex
                        data-testid="grid-cell"
                        role="row" // role="row" important for accessibility
                        key={key}
                        style={{
                          ...style,
                          padding,
                          pointerEvents: 'visible',
                        }}
                      >
                        {!isVisible && placeholder?.(columnIndex)
                          ? placeholder?.(columnIndex)
                          : item}
                      </Flex>
                    )
                  }}
                  columnCount={columnCount}
                  columnWidth={Math.trunc(width / columnCount)}
                  rowCount={Math.ceil(itemCount / columnCount)}
                  rowHeight={rowHeight}
                  tabIndex={tabIndex || -1}
                />
              )
            }}
          </AutoSizer>
        )}
      </WindowScroller>
    </Flex>
  )
}
